import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Search } from 'lucide-react'
import {
  useConversations,
  formatRelativeTime,
  type Conversation,
  type InboxFilter,
  type InboxScope,
} from './use-inbox'

interface ConversationListProps {
  filter: InboxFilter
  onFilterChange: (filter: InboxFilter) => void
  scope: InboxScope
  onScopeChange: (scope: InboxScope) => void
  selectedLeadId: string | null
  onSelect: (conversation: Conversation) => void
}

const FILTER_TABS: { value: InboxFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'needs_attention', label: 'Attention' },
  { value: 'replied', label: 'Replied' },
  { value: 'escalated', label: 'Escalated' },
]

// Source scope — a second, orthogonal row under the status tabs. 'DCA' is
// every conversation DealCloser drove (campaign leads + engagement DMs);
// 'Direct' is organic LinkedIn messages the automation never touched.
const SCOPE_TABS: { value: InboxScope; label: string; title: string }[] = [
  { value: 'all', label: 'All sources', title: 'DCA outreach and direct LinkedIn messages' },
  { value: 'campaign', label: 'DCA', title: 'Conversations started by your DealCloser outreach' },
  { value: 'direct', label: 'Direct', title: 'LinkedIn conversations unrelated to DealCloser' },
]

function getStatusDot(status: Conversation['status']): string {
  switch (status) {
    case 'escalated':
      return 'bg-red-500'
    case 'replied':
      return 'bg-green-500'
    case 'active':
      return 'bg-yellow-500'
    case 'paused':
      return 'bg-gray-400'
    default:
      return 'bg-gray-400'
  }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '…'
}

export function ConversationList({
  filter,
  onFilterChange,
  scope,
  onScopeChange,
  selectedLeadId,
  onSelect,
}: ConversationListProps) {
  const [search, setSearch] = useState('')
  const { data: conversations = [], isLoading } = useConversations(filter, scope)

  const filtered = search.trim()
    ? conversations.filter(
        (c) =>
          c.lead_name.toLowerCase().includes(search.toLowerCase()) ||
          c.lead_company.toLowerCase().includes(search.toLowerCase()),
      )
    : conversations

  return (
    <div className="flex h-full w-[280px] shrink-0 flex-col border-r bg-background">
      {/* Filter tabs */}
      <div className="border-b px-2 pt-3">
        <div className="flex gap-0.5">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onFilterChange(tab.value)}
              className={cn(
                'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                filter === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Source scope */}
        <div className="mt-1.5 flex gap-0.5 rounded-md bg-muted/40 p-0.5">
          {SCOPE_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onScopeChange(tab.value)}
              title={tab.title}
              className={cn(
                'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                scope === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative my-2">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search leads…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="space-y-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3 border-b px-3 py-3">
                <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                  <div className="h-2.5 w-36 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">No conversations found</p>
          </div>
        )}

        {!isLoading &&
          filtered.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => onSelect(conversation)}
              className={cn(
                'flex w-full gap-3 border-b px-3 py-3 text-left transition-colors hover:bg-muted/50',
                selectedLeadId === conversation.lead_id && 'bg-muted',
              )}
            >
              {/* Avatar */}
              <div className="relative shrink-0">
                <Avatar className="h-9 w-9">
                  {conversation.lead_avatar && (
                    <img src={conversation.lead_avatar} alt={conversation.lead_name} />
                  )}
                  <AvatarFallback className="text-xs">
                    {getInitials(conversation.lead_name)}
                  </AvatarFallback>
                </Avatar>
                {/* Status dot */}
                <span
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background',
                    getStatusDot(conversation.status),
                  )}
                />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="truncate text-sm font-semibold leading-none">
                    {conversation.lead_name}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {formatRelativeTime(conversation.last_message_at)}
                  </span>
                </div>
                <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                  {conversation.lead_company}
                </span>
                <div className="mt-1 flex items-center gap-1">
                  {conversation.unread && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  )}
                  <span className="truncate text-xs text-muted-foreground">
                    {truncate(conversation.last_message, 60)}
                  </span>
                </div>
                {conversation.status === 'escalated' && (
                  <Badge variant="destructive" className="mt-1 px-1 py-0 text-[10px]">
                    Escalated
                  </Badge>
                )}
                {conversation.manually_handled_at && (
                  <Badge
                    variant="outline"
                    className="mt-1 border-blue-500/30 px-1 py-0 text-[10px] text-blue-400"
                  >
                    Handled by you
                  </Badge>
                )}
              </div>
            </button>
          ))}
      </div>
    </div>
  )
}
